import {
  AiAgentServiceSettingForPayee,
  AiServiceSetting,
  ApPayeeReconciliationSettingForPayee,
  ApPayeeReconciliationSettingForPayeeStatus,
  GetApPayeeInfoResponseAgentReceiveSetting,
  GetApPayeeInfoResponseApInvoiceTransactionDefaultValue,
  GetApPayeeInfoResponseApPayeeContactSetting,
  GetApPayeeInfoResponseContactOfficeMember,
  GetApPayeeInfoResponseContactOfficeMembersItem,
  GetApPayeeInfoResponseSupplierUploadSetting,
  UpdateApPayeeReconciliationSettingRequest,
} from 'ap-openapi';

export enum SECTION_KEYS {
  PIC = 'PIC',
  GEN = 'GEN',
  TRANSACTION = 'TRANSACTION',
  EMAIL = 'EMAIL',
  AI_AGENT = 'AI_AGENT',
  AP_AI_SERVICE = 'AP_AI_SERVICE',
  RECONCILIATION = 'RECONCILIATION',
  NULL = 'NULL',
}

export enum InvoiceReceiveEmailProcessType {
  PAYMENT_REQUEST = 'payment_request',
  RECEIVED_INVOICE = 'received_invoice',
}

type baseDataProps = {
  id: string;
};

export type GeneralInformationData = baseDataProps & {
  display_name: string;
};

export type PICAndContractDetailData = baseDataProps & {
  ap_payee_contact_setting?: GetApPayeeInfoResponseApPayeeContactSetting;
  contact_office_members?: GetApPayeeInfoResponseContactOfficeMembersItem[];
  contact_person_email?: string;
  contact_person_full_address?: string;
  contact_person_name?: string;
  contact_person_phone_number?: string;
  dept_name?: string;
};

export type WebReceivingMethodData = {
  supplierUploadSetting?: GetApPayeeInfoResponseSupplierUploadSetting;
  is_supplier_upload_setting_enabled?: boolean;
  is_payee_active?: boolean;
};

export type ProxyReceivingMethodData = {
  is_proxy_receipt_receiving_method_enabled?: boolean;
  agentReceiveSetting?: GetApPayeeInfoResponseAgentReceiveSetting;
};

export type EmailReceivingMethodData = {
  is_invoice_receive_email_setting_existed?: boolean;
  invoice_receive_email_address?: string;
  invoice_receive_email_process_type?: InvoiceReceiveEmailProcessType;
  contact_office_member?: GetApPayeeInfoResponseContactOfficeMember;
  is_invoice_receive_mail_address_issued?: boolean;
};

export type ReceivingMethodData = baseDataProps & {
  webReceivingMethod: WebReceivingMethodData;
  proxyReceivingMethod: ProxyReceivingMethodData;
  emailReceivingMethod: EmailReceivingMethodData;
};

export type AiAgentSettingsData = baseDataProps & {
  ai_agent_service_settings?: AiAgentServiceSettingForPayee[];
};

/**
 * Reconciliation (突合サービス連携) integration setting for this payee.
 * `ap_payee_reconciliation_setting` is null while the office has not enabled the
 * service — the section and its anchor item are hidden in that case.
 */
export type ReconciliationSettingData = baseDataProps & {
  ap_payee_reconciliation_setting?: ApPayeeReconciliationSettingForPayee;
};

export const RECONCILIATION_DEFAULT_STATUS =
  ApPayeeReconciliationSettingForPayeeStatus.manual;

export const RECONCILIATION_STATUS_LABEL_KEYS: Record<
  ApPayeeReconciliationSettingForPayeeStatus,
  string
> = {
  [ApPayeeReconciliationSettingForPayeeStatus.manual]:
    'payee_reconciliation_setting_status_manual',
  [ApPayeeReconciliationSettingForPayeeStatus.auto_send]:
    'payee_reconciliation_setting_status_auto_send',
  [ApPayeeReconciliationSettingForPayeeStatus.not_send]:
    'payee_reconciliation_setting_status_not_send',
};

export const RECONCILIATION_STATUS_CAPTION_KEYS: Record<
  ApPayeeReconciliationSettingForPayeeStatus,
  string
> = {
  [ApPayeeReconciliationSettingForPayeeStatus.manual]:
    'payee_reconciliation_setting_status_manual_caption',
  [ApPayeeReconciliationSettingForPayeeStatus.auto_send]:
    'payee_reconciliation_setting_status_auto_send_caption',
  [ApPayeeReconciliationSettingForPayeeStatus.not_send]:
    'payee_reconciliation_setting_status_not_send_caption',
};

/**
 * Display order of the status choices in the edit form. Kept separate from
 * RECONCILIATION_STATUS_LABEL_KEYS because the API enum order (manual first)
 * is not the order the options are shown in.
 */
export const RECONCILIATION_STATUS_ORDER = [
  ApPayeeReconciliationSettingForPayeeStatus.auto_send,
  ApPayeeReconciliationSettingForPayeeStatus.manual,
  ApPayeeReconciliationSettingForPayeeStatus.not_send,
] as const;

export type PaymentRuleData = {
  payment_closing_date?: string; // Formatted string like "31日"
  payment_month?: string; // Translated string like "翌月"
  payment_due_date?: string; // Formatted string like "31日"
  payment_holiday_rule?: string; // Translated string like "前倒し"
};

export type DataMaster = {
  generalInformation: GeneralInformationData;
  PICAndContractDetail: PICAndContractDetailData;
  receivingMethod: ReceivingMethodData;
  reconciliationSetting: ReconciliationSettingData;
  aiAgentSettings: AiAgentSettingsData;
  apAiServiceSettings: AiServiceSetting[];
  paymentRule: PaymentRuleData;
  transactionDefaultValue?: GetApPayeeInfoResponseApInvoiceTransactionDefaultValue;
  isEditable?: boolean;
};

export enum AnchorItemKey {
  GENERAL_INFORMATION = 'general-information',
  PIC_AND_CONTACT_INFORMATION = 'pic-and-contact-information',
  RECEIVING_METHOD = 'receiving-method',
  RECONCILIATION = 'reconciliation',
  AI_AGENT = 'ai-agent',
  DEFAULT_PAYMENT_METHOD = 'default-payment-method',
  PAYMENT_RULE = 'payment-rule',
  TRANSACTION_DEFAULT_VALUE = 'transaction-default-value',
  AP_AI_SERVICE_SETTING = 'ap-ai-service-setting',
}

export type PICAndContactInformationForm = {
  contact_office_member_ids: string[];
  contact_person_name?: string;
  dept_name?: string;
  contact_person_email?: string;
  contact_person_phone_number?: string;
  postal_code?: string;
  prefecture_id?: number;
  address?: string;
};

export type TransactionDetailForm = {
  name?: string;
  dept_id?: string;
  ap_item_id?: string;
  project_id?: string;
  is_tax_included?: boolean;
  default_currency?: string;
};

export type ApInvoiceReceiveEmailSetting = {
  process_invoice_type?: InvoiceReceiveEmailProcessType;
};

export type EmailReceivingMethodForm = {
  contact_office_member_id?: string;
  ap_invoice_receive_email_setting?: ApInvoiceReceiveEmailSetting;
};

export type AiAgentSettingsForm = {
  invoice_download_agent_enabled: boolean;
};

/**
 * Sent as-is under `ap_payee` on PATCH /api/js/ap_payees/{id}/info — the shape
 * already matches the request schema, so transformPayeeFormData passes it through.
 */
export type ReconciliationSettingForm = {
  ap_payee_reconciliation_setting: UpdateApPayeeReconciliationSettingRequest;
};

/**
 * Mapping between SECTION_KEYS and their corresponding AnchorItemKey
 * This allows automatic anchor resolution from the editing section state
 */
export const SECTION_TO_ANCHOR_MAP: Record<SECTION_KEYS, AnchorItemKey | null> =
  {
    [SECTION_KEYS.PIC]: AnchorItemKey.PIC_AND_CONTACT_INFORMATION,
    [SECTION_KEYS.TRANSACTION]: AnchorItemKey.TRANSACTION_DEFAULT_VALUE,
    [SECTION_KEYS.AI_AGENT]: AnchorItemKey.AI_AGENT,
    [SECTION_KEYS.AP_AI_SERVICE]: AnchorItemKey.AP_AI_SERVICE_SETTING,
    [SECTION_KEYS.RECONCILIATION]: AnchorItemKey.RECONCILIATION,
    [SECTION_KEYS.EMAIL]: AnchorItemKey.RECEIVING_METHOD,
    [SECTION_KEYS.GEN]: null, // General info is not editable
    [SECTION_KEYS.NULL]: null, // No section selected
  };
